import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Card, CardBody, Row, Col } from "reactstrap";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import CarePlanReviewReportShimmer from "./CarePlanReviewReportShimmer";
import CarePlanReviewFilters from "./CarePlanReviewFilters";
import CptDashboardSkeletonOverlay from "../common/CptDashboardSkeletonOverlay";
import "../PatientCPTRequirements/ReportComponents.css";
import "./CarePlanReviewReport.css";

const DEFAULT_PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.tz(moment.tz.guess()).format("MMM DD, YYYY") : value;
};

const getPatientDashboardPath = (patient = {}) => {
  const patientId = patient?._id;
  if (!patientId) return "";
  const fullName = String(patient?.patientName || "").trim();
  const [lastName = "", firstName = ""] = fullName.split(",").map((value) => String(value || "").trim());
  const sanitize = (value) =>
    String(value || "")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      .toLowerCase();
  const slug = `${sanitize(firstName)}-${sanitize(lastName)}`.replace(/^-|-$/g, "");
  return `/dashboard/${slug || "patient"}/${patientId}`;
};

const PatientNameCell = ({ name, email, phone }) => (
  <div className="patient-profile-cell">
    <div className="patient-profile-content">
      <div className="patient-profile-name-row">
        <span className="patient-profile-name-link">{name || "-"}</span>
      </div>
      <div className="patient-profile-meta">
        <i className="mdi mdi-email-outline"></i>
        <span>{email || "-"}</span>
      </div>
      <div className="patient-profile-meta">
        <i className="mdi mdi-phone-outline"></i>
        <span>{phone || "-"}</span>
      </div>
    </div>
  </div>
);

const DateCell = ({ date, iconClass }) => (
  <div className="date-cell-wrapper">
    <i className={`mdi ${iconClass} date-icon`}></i>
    <span className="date-text">{date}</span>
  </div>
);

const DaysUntilDueCell = ({ days }) => {
  if (days === null || days === undefined || days === "") return "-";
  const numericDays = Number(days);
  if (Number.isNaN(numericDays)) return "-";
  const isOverdue = numericDays < 0;
  return (
    <span className={`cpr-days-value ${isOverdue ? "overdue" : ""}`}>
      {isOverdue ? `${Math.abs(numericDays)} days overdue` : `${numericDays} days`}
    </span>
  );
};

const StatusCell = ({ status }) => (
  <span className={`patient-status-badge ${status === "Overdue" ? "overdue" : "due-soon"}`}>
    {status || "-"}
  </span>
);

const ViewActionCell = ({ row }) => {
  const path = getPatientDashboardPath(row);
  return (
    <button
      type="button"
      className="patient-details-btn patient-details-btn-profile"
      disabled={!path}
      onClick={(e) => {
        e.stopPropagation();
        if (!path) return;
        window.location.href = path;
      }}
    >
      <i className="mdi mdi-account-details-outline"></i>
      <span>View</span>
    </button>
  );
};

const columns = [
  {
    dataField: "patientName",
    text: "Patient Name",
    formatter: (cell, row) => <PatientNameCell name={cell} email={row?.email} phone={row?.phone} />,
    headerStyle: () => ({ minWidth: "230px" })
  },
  {
    dataField: "mainCareProviderName",
    text: "Main Care Provider",
    formatter: (cell) => <span className="date-text-display">{cell}</span>
  },
  {
    dataField: "organization",
    text: "Client",
    formatter: (cell) => (
      <div className="org-cell-wrapper">
        <span className="org-icon"><i className="mdi mdi-hospital-building"></i></span>
        <span className="org-text">{cell}</span>
      </div>
    )
  },
  {
    dataField: "carePlanCreatedDate",
    text: "Created Date",
    sort: true,
    formatter: (cell) => <DateCell date={formatDate(cell)} iconClass="mdi-calendar-outline" />
  },
  {
    dataField: "carePlanLastUpdatedDate",
    text: "Last Updated",
    sort: true,
    formatter: (cell) => <DateCell date={formatDate(cell)} iconClass="mdi-calendar-edit" />
  },
  {
    dataField: "reviewDueDate",
    text: "Review Due Date",
    sort: true,
    formatter: (cell) => <DateCell date={formatDate(cell)} iconClass="mdi-calendar-clock" />
  },
  {
    dataField: "daysUntilReviewDue",
    text: "Days Until Due",
    sort: true,
    formatter: (cell) => <DaysUntilDueCell days={cell} />
  },
  {
    dataField: "status",
    text: "Status",
    formatter: (cell) => <StatusCell status={cell} />
  },
  {
    dataField: "patientActions",
    text: "Action",
    isDummyField: true,
    formatter: (cell, row) => <ViewActionCell row={row} />,
    headerStyle: () => ({ minWidth: "110px", textAlign: "center" }),
    headerClasses: "text-center",
    classes: "text-center"
  }
];

const EMPTY_FILTERS = { year: "", month: "", clientId: "", careproviderIds: [] };
const EMPTY_SORT = { sortField: "", sortOrder: "" };

const CarePlanReviewReport = ({
  dispatch,
  isActiveTab,
  isAdmin,
  carePlanReviewReportData,
  ongoingCarePlanReviewReportRequest,
  clientAll,
  careProviderAll
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState(EMPTY_SORT);

  const fetchReport = (nextPage = page, nextPageSize = pageSize, nextFilters = filters, nextSort = sort) => {
    const payload = {
      page: nextPage - 1,
      limit: nextPageSize
    };
    if (nextFilters.year) payload.year = nextFilters.year;
    if (nextFilters.month) payload.month = nextFilters.month;
    if (isAdmin && nextFilters.clientId) payload.clientId = nextFilters.clientId;
    if (isAdmin && nextFilters.careproviderIds && nextFilters.careproviderIds.length) {
      payload.careproviderIds = nextFilters.careproviderIds.join(",");
    }
    if (nextSort.sortField && nextSort.sortOrder) {
      payload.sortField = nextSort.sortField;
      payload.sortOrder = nextSort.sortOrder;
    }
    dispatch({
      type: "DO_GET_CARE_PLAN_REVIEW_REPORT",
      payload
    });
  };

  useEffect(() => {
    dispatch({ type: "RESET_CARE_PLAN_REVIEW_REPORT" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isActiveTab || !isAdmin) return;
    dispatch({ type: "DO_GET_CAREPROVIDER" });
    dispatch({ type: "DO_GET_CLIENT_LIST_NEW" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActiveTab]);

  const clientOptions = useMemo(() => (
    (clientAll || [])
      .filter((item) => item?._id && item?.organization)
      .map((item) => ({ value: item._id, label: item.organization }))
  ), [clientAll]);

  const careProviderOptions = useMemo(() => (
    (careProviderAll || [])
      .filter((item) => item?._id)
      .map((item) => ({ value: item._id, label: `${item.fname || ""} ${item.lname || ""}`.trim() }))
  ), [careProviderAll]);

  const handleFilterApply = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    fetchReport(1, pageSize, nextFilters, sort);
  };

  const reportData = carePlanReviewReportData || {};
  const patients = Array.isArray(reportData?.patients) ? reportData.patients : [];

  const rows = useMemo(() => (
    patients.map((patient) => ({
      ...patient,
      mainCareProviderName: patient?.mainCareProvider?.name || "-",
      organization: patient?.client?.organization || "-"
    }))
  ), [patients]);

  const totalRecords = Number(reportData?.total || 0);
  const dueSoonCount = Number(reportData?.dueSoonCount || 0);
  const overdueCount = Number(reportData?.overdueCount || 0);

  const isLoading = ongoingCarePlanReviewReportRequest;
  const hasLoadedOnce = Boolean(carePlanReviewReportData);
  const showSkeleton = isLoading && !hasLoadedOnce;
  const isRefetching = isLoading && hasLoadedOnce;

  const handleTableChange = (type, { page: nextPage, sizePerPage, sortField, sortOrder }) => {
    if (type === "sort") {
      const nextSort = { sortField: sortField || "", sortOrder: sortOrder || "" };
      setSort(nextSort);
      setPage(1);
      fetchReport(1, pageSize, filters, nextSort);
      return;
    }
    if (type === "pagination") {
      setPage(nextPage);
      setPageSize(sizePerPage);
      fetchReport(nextPage, sizePerPage, filters, sort);
    }
  };

  const customTotal = (from, to, size) => (
    <span className="react-bootstrap-table-pagination-total">
      Showing {from} to {to} of {size} Results
    </span>
  );

  const paginationOptions = {
    bootstrap4: true,
    page,
    sizePerPage: pageSize,
    totalSize: totalRecords,
    firstPageText: "First",
    prePageText: "Back",
    nextPageText: "Next",
    lastPageText: "Last",
    alwaysShowAllBtns: false,
    withFirstAndLast: true,
    hideSizePerPage: false,
    hidePageListOnlyOnePage: true,
    showTotal: totalRecords > 0,
    paginationTotalRenderer: customTotal,
    sizePerPageList: [
      { text: "10", value: 10 },
      { text: "25", value: 25 },
      { text: "50", value: 50 },
      { text: "100", value: 100 }
    ]
  };

  return (
    <div className={`cpt-performance-wrapper report-container cpt-dash-report-surface cpr-report ${isRefetching ? "cpt-dash-report-surface--busy" : ""}`}>
      {isRefetching && (
        <CptDashboardSkeletonOverlay>
          <CarePlanReviewReportShimmer />
        </CptDashboardSkeletonOverlay>
      )}

      <CarePlanReviewFilters
        isAdmin={isAdmin}
        year={filters.year}
        month={filters.month}
        clientId={filters.clientId}
        careproviderIds={filters.careproviderIds}
        clientOptions={clientOptions}
        careProviderOptions={careProviderOptions}
        onApply={handleFilterApply}
        loading={isLoading}
      />

      {showSkeleton ? (
        <CarePlanReviewReportShimmer />
      ) : !hasLoadedOnce ? (
        <div className="cpt-select-prompt">
          <div className="cpt-select-prompt-icon">
            <i className="mdi mdi-clipboard-check-outline"></i>
          </div>
          <h5>Select filters to view the care plan review report</h5>
          <p>Choose Year, Month{isAdmin ? ", Client, or Care Provider" : ""} above, then click <strong>Filter</strong> to load the data.</p>
        </div>
      ) : (
        <>
          <Row className="mb-3">
            <Col md="4">
              <Card className="stat-card-simple na-kpi">
                <CardBody>
                  <div className="stat-content">
                    <span className="stat-label">Total Patients</span>
                    <h2 className="stat-number">{totalRecords}</h2>
                  </div>
                  <div className="stat-icon-wrap blue">
                    <i className="mdi mdi-account-group"></i>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card className="stat-card-simple na-kpi">
                <CardBody>
                  <div className="stat-content">
                    <span className="stat-label">Due Soon</span>
                    <h2 className="stat-number text-warning">{dueSoonCount}</h2>
                  </div>
                  <div className="stat-icon-wrap amber">
                    <i className="mdi mdi-calendar-clock"></i>
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col md="4">
              <Card className="stat-card-simple na-kpi">
                <CardBody>
                  <div className="stat-content">
                    <span className="stat-label">Overdue</span>
                    <h2 className="stat-number text-danger">{overdueCount}</h2>
                  </div>
                  <div className="stat-icon-wrap red">
                    <i className="mdi mdi-calendar-alert"></i>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card className="details-table-card-modern">
            <CardBody>
              <div className="chart-header-modern">
                <div className="chart-icon-modern"><i className="mdi mdi-clipboard-check-outline"></i></div>
                <div className="chart-header-copy-modern">
                  <h4>Care Plan Review and Update</h4>
                  <p>Patients whose care plan review is due within the next 60 days or already overdue.</p>
                </div>
              </div>

              <BootstrapTable
                bootstrap4
                remote={{ pagination: true, sort: true }}
                bordered={false}
                wrapperClasses="table-responsive"
                classes="mpr-patient-table modern-table-enterprise"
                keyField="_id"
                data={rows}
                columns={columns}
                pagination={paginationFactory(paginationOptions)}
                onTableChange={handleTableChange}
                noDataIndication={() => (
                  <div className="mpr-empty-row">No patients currently need care plan review.</div>
                )}
              />
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

const mapStateToProps = ({ careprovider, user }) => {
  const role = user?.userDetails?.role;
  return {
    isAdmin: role === "admin" || role === "support",
    carePlanReviewReportData: careprovider.carePlanReviewReportData,
    ongoingCarePlanReviewReportRequest: careprovider.ongoingCarePlanReviewReportRequest,
    clientAll: careprovider.clientAll,
    careProviderAll: careprovider.careProviderAll
  };
};

export default connect(mapStateToProps)(CarePlanReviewReport);
